import { useNavigate, useSearch } from "@tanstack/react-router";
import { useSupplierPayments } from "@/api/queries";
import type { SupplierPaymentSummary } from "@/api/types";
import { PermittedAction } from "@/components/PermittedAction";
import type { Column } from "@/components/list/DataTable";
import type { ListSearch } from "@/components/list/listQuery";
import { ListScreen, listQueryFor } from "@/components/list/ListScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";

export function PaymentsListPage() {
  const say = useTaxWords();
  const search = useSearch({ from: "/purchases/payments" }) as ListSearch;
  const navigate = useNavigate();
  const { data, isPending, error, refetch } = useSupplierPayments(listQueryFor(search));

  const columns: Array<Column<SupplierPaymentSummary>> = [
    {
      key: "number",
      header: "Number",
      width: "13ch",
      numeric: true,
      render: (row) => row.number ?? <span className="text-fg-muted">Draft</span>,
    },
    { key: "supplier", header: "Supplier", render: (row) => row.supplierName },
    { key: "date", header: "Date", width: "12ch", render: (row) => formatDate(row.date) },
    {
      key: "method",
      header: "Method",
      render: (row) => (row.reference ? `${row.method} ${row.reference}` : row.method),
    },
    {
      key: "applied",
      header: "Applied to",
      render: (row) =>
        row.appliedTo.length > 0 ? (
          <span className="num">{row.appliedTo.join(", ")}</span>
        ) : (
          <span className="text-fg-muted">
            {row.status === "draft" ? "Advance" : "Held as advance"}
          </span>
        ),
    },
    {
      key: "withheld",
      header: say("TDS withheld"),
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.withheld),
    },
    {
      key: "net",
      header: "Paid",
      align: "right",
      numeric: true,
      render: (row) => formatAmount(row.net),
    },
  ];

  return (
    <ListScreen
      route="/purchases/payments"
      title="Payments"
      subtitle={data ? `${data.total} this fiscal year` : "Loading payments"}
      actions={
        <PermittedAction
          variant="secondary"
          leftIcon="Plus"
          permission="purchases.payments.create"
          onClick={() =>
            void navigate({ to: "/purchases/payments/new", search: { bill: undefined } })
          }
        >
          Record payment
        </PermittedAction>
      }
      statuses={null}
      columns={columns}
      rows={data?.items ?? []}
      total={data?.total ?? 0}
      hasNext={data?.next != null}
      loading={isPending}
      error={error}
      onRetry={() => void refetch()}
      rowKey={(row) => row.id}
      rowLabel={(row) =>
        `Payment ${row.number ?? "draft"}, ${row.supplierName}, ${formatAmount(row.net)}`
      }
      onRowClick={(row) => void navigate({ to: "/purchases/payments/$id", params: { id: row.id } })}
      searchPlaceholder="Search number, supplier or method"
      empty={{
        icon: "ArrowUpRight",
        title: "No payments yet",
        description:
          "Recording a payment settles bills. Anything unapplied is held as a supplier advance.",
      }}
      filteredEmpty={{
        icon: "Search",
        title: "No payments match these filters",
        description: "Try a wider date range, or clear the filters to see everything again.",
      }}
    />
  );
}
